import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AppStateService } from '../../../core/services/app-state.service';
import { MailService } from '../../../../services/mail.service';
import {
  CampoRicerca,
  emailDestinatari,
  Mail,
  MailType,
  MittenteSuggerito,
} from '../../../../models/mail.model';

interface CampoRicercaOption {
  value: CampoRicerca;
  label: string;
}

const CAMPI_RICERCA: CampoRicercaOption[] = [
  { value: 'MITTENTE', label: 'Mittente' },
  { value: 'DESTINATARIO', label: 'Destinatario' },
  { value: 'OGGETTO', label: 'Oggetto' },
  { value: 'CORPO', label: 'Corpo' },
  { value: 'ETICHETTA', label: 'Etichetta' },
];

const SEZIONE_LABEL: Record<MailType, string> = {
  INBOX: 'Ricevuta',
  OUTBOX: 'Inviata',
  DRAFT: 'Bozza',
  ARCHIVED: 'Archiviata',
  ACCETTAZIONE: 'Accettazione',
  NON_ACCETTAZIONE: 'Non accettazione',
  AVVENUTA_CONSEGNA: 'Consegna',
  ERRORE_CONSEGNA: 'Errore consegna',
  PREAVVISO_ERRORE_CONSEGNA: 'Preavviso errore',
  RILEVAZIONE_VIRUS: 'Virus',
  PRESA_IN_CARICO: 'Presa in carico',
  SCONOSCIUTA: 'Ricevuta',
};

@Component({
  selector: 'pec-search-panel',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './search-panel.component.html',
  styleUrls: ['./search-panel.component.scss', '../../../../assets/scss/global.style.scss'],
})
export class SearchPanelComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly mailService = inject(MailService);
  readonly state = inject(AppStateService);

  readonly campiRicerca = CAMPI_RICERCA;
  readonly sezioneLabel = SEZIONE_LABEL;

  readonly isOpen = signal(false);
  readonly selectedFields = signal<Set<CampoRicerca>>(new Set());
  readonly senderSuggestions = signal<MittenteSuggerito[]>([]);
  readonly results = signal<Mail[]>([]);
  readonly loading = signal(false);

  private searchTimeout?: ReturnType<typeof setTimeout>;

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  onFocus() {
    this.isOpen.set(true);
    this.runSearch();
  }

  onQueryChange(value: string) {
    this.state.query.set(value);
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.runSearch(), 300);
  }

  clearQuery() {
    this.state.query.set('');
    this.senderSuggestions.set([]);
    this.results.set([]);
  }

  toggleField(field: CampoRicerca) {
    this.selectedFields.update(prev => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
    this.runSearch();
  }

  isFieldSelected(field: CampoRicerca): boolean {
    return this.selectedFields().has(field);
  }

  /**
   * Titolo della prima sezione del pannello: cambia in base al filtro attivo, dato che i
   * suggerimenti vengono dalla stessa rubrica sia per mittenti che per destinatari.
   */
  searchSectionTitle(): string {
    if (this.isFieldSelected('DESTINATARIO')) return 'Destinatari';
    if (this.isFieldSelected('MITTENTE')) return 'Mittenti';
    return 'Indirizzi';
  }

  pickSuggestedSender(sender: MittenteSuggerito) {
    this.state.query.set(sender.mittente);
    const campo: CampoRicerca = this.isFieldSelected('DESTINATARIO') ? 'DESTINATARIO' : 'MITTENTE';
    this.selectedFields.update(prev => new Set(prev).add(campo));
    this.runSearch();
  }

  isResultClickable(msg: Mail): boolean {
    return true;          // ogni tipo ha una destinazione
  }

  openResult(msg: Mail) {
  if (msg.tipo === 'DRAFT') {
    this.state.openCompose({
      mode: 'new', draftId: msg.id, to: emailDestinatari(msg).join(', '),
      from: msg.mittente, subject: msg.oggetto, body: msg.corpo,
    });
  } else {
    const rotta =
      msg.tipo === 'INBOX' ? '/inbox/detail'
      : msg.tipo === 'OUTBOX' ? '/sent/detail'
      : '/archive/detail';          // ARCHIVED e tutte le ricevute
    this.router.navigate([rotta], { queryParams: { id: msg.id } });
  }
  this.isOpen.set(false);
}

  onEnter() {
    const query = this.state.query().trim();
    if (!query) return;
    const campi =
      this.selectedFields().size === 0
        ? this.campiRicerca.map(c => c.value)
        : [...this.selectedFields()];
    this.isOpen.set(false);
    this.router.navigate(['/search'], { queryParams: { query, campi: campi.join(',') } });
  }

  private runSearch() {
    const query = this.state.query().trim();
    if (!query) {
      this.senderSuggestions.set([]);
      this.results.set([]);
      return;
    }

    // "Destinatario" attivo → suggerisce destinatari reali; altrimenti (compreso nessun filtro,
    // che di default mostra i mittenti come in origine) → suggerisce mittenti.
    const suggestions$ = this.isFieldSelected('DESTINATARIO')
      ? this.mailService.suggestRecipients(query)
      : this.mailService.suggestSenders(query);
    suggestions$.subscribe({
      next: senders => this.senderSuggestions.set(this.dedupeByMittente(senders)),
      error: () => this.senderSuggestions.set([]),
    });

    const campi =
      this.selectedFields().size === 0
        ? this.campiRicerca.map(c => c.value)
        : [...this.selectedFields()];

    this.loading.set(true);
    this.mailService.searchMessages(query, campi, { pageIndex: 0, pageSize: 20 }).subscribe({
      next: page => {
        this.results.set(page.content ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.results.set([]);
        this.loading.set(false);
      },
    });
  }

  /**
   * Lo stesso indirizzo email può comparire più volte con un nomeMittente diverso (o assente)
   * da un messaggio all'altro: ne teniamo uno solo per indirizzo, preferendo la variante che ha
   * un nome valorizzato.
   */
  private dedupeByMittente(mittenti: MittenteSuggerito[]): MittenteSuggerito[] {
    const visti = new Map<string, MittenteSuggerito>();
    for (const m of mittenti) {
      const chiave = m.mittente.toLowerCase();
      const esistente = visti.get(chiave);
      if (!esistente || (!esistente.nomeMittente && m.nomeMittente)) {
        visti.set(chiave, m);
      }
    }
    return [...visti.values()];
  }
}
